import React, { FunctionComponent, useContext, useMemo, useState } from "react";
import { Grid, lighten, makeStyles, Theme, useTheme } from "@material-ui/core";
import { CategoryItemMenu } from "./category-item-menu";
import { animated, useSpring } from "react-spring";
import { DARK_TEXT_COLOR, LIGHT_GRAY } from "../../theme/theme";
import { MenuCategoryDictionary } from "../../models/categories";
import { NutritionContext } from "../../pages/nutrition/context/nutrition-context";
import { MenuItemCategory } from "../../models/types/category-types";

const BORDER_RADIUS_SPACING = 3;
const ICON_SIZE = 35;
const PADDING_SPACING = 2;
const FONT_SIZE = 18;
const LINE_HEIGHT = 1.2;
const FONT_MARGIN_SPACING = 1.5;
const ICON_CONTAINER_HEIGHT = 60;

const SHADOW_COLOR = LIGHT_GRAY;

export const getCategoryItemHeight = (theme: Theme) => {
  return FONT_SIZE * LINE_HEIGHT + theme.spacing(FONT_MARGIN_SPACING) + ICON_SIZE + theme.spacing(PADDING_SPACING * 2);
};

const useStyles = makeStyles((theme: Theme) => ({
  itemWrapper: {
    cursor: "pointer",
    padding: theme.spacing(3),
    paddingTop: 0,
    marginTop: theme.spacing(3),
    maxHeight: theme.spacing(18),
    borderRadius: theme.spacing(BORDER_RADIUS_SPACING),
  },
  item: {
    background: theme.palette.common.white,
    zIndex: 99,
    boxShadow: `5px 5px 20px 5px ${SHADOW_COLOR}`,
    borderRadius: theme.spacing(BORDER_RADIUS_SPACING),
    position: "relative",
  },
  iconContainer: { height: ICON_CONTAINER_HEIGHT },
}));

interface ICategoryItemProps {
  category: MenuItemCategory;
  active: boolean;
}

export const CategoryItem: FunctionComponent<ICategoryItemProps> = (props) => {
  const { category, active } = props;
  const { Icon, color } = MenuCategoryDictionary[category];
  const classes = useStyles();
  const theme = useTheme();
  const { changeActiveCategories } = useContext(NutritionContext);

  const [isMouseOnMenu, setIsMouseOnMenu] = useState<boolean>(false);
  const [isMouseOnItem, setIsMouseOnItem] = useState<boolean>(false);

  const open = useMemo(() => isMouseOnMenu || isMouseOnItem, [isMouseOnMenu, isMouseOnItem]);
  const hasSubcategories = useMemo(() => MenuCategoryDictionary[category].subcategories.length > 0, [category]);

  const backgroundColor = useMemo(() => {
    if (active || open) {
      return lighten(color!, 0.3);
    }

    return theme.palette.common.white;
  }, [theme, color, active, open]);

  const style = useSpring({
    backgroundColor,
    boxShadow: `0 0px 20px 0 ${LIGHT_GRAY}`,
  });

  // Reset active index on category selection by user
  const onClick = useMemo(() => (hasSubcategories ? undefined : () => changeActiveCategories(category, undefined, 1)), [
    hasSubcategories,
    changeActiveCategories,
    category,
  ]);

  return (
    <Grid
      className={classes.itemWrapper}
      onMouseEnter={() => setIsMouseOnItem(true)}
      onMouseLeave={() => setIsMouseOnItem(false)}
      onClick={onClick}
    >
      <animated.div style={style} className={classes.item}>
        <Grid
          item
          container
          direction="column"
          alignItems="center"
          justify={"center"}
          className={classes.iconContainer}
        >
          {Icon && <Icon height={ICON_SIZE} width={ICON_SIZE} color={DARK_TEXT_COLOR} />}
        </Grid>
        <CategoryItemMenu
          open={open && hasSubcategories}
          category={category}
          onMouseEnter={() => setIsMouseOnMenu(true)}
          onMouseLeave={() => setIsMouseOnMenu(false)}
        />
      </animated.div>
    </Grid>
  );
};
